import { ErrorCode, ErrorName, ErrorType, PlayerError, } from '@tunein/web-media-player';
import { pick } from 'es-toolkit';
import { api } from '../singletons';
import { ERROR_CODES } from './AdReporter/constants';
import { AD_MEDIUM_TYPES } from './constants';
const APS_BIDDING_FAILURE_METADATA = {
    singleBid: {
        errorCode: '87',
        errorMessage: 'No parameters received.',
    },
    allBids: {
        errorCode: '85',
        errorMessage: 'Amazon keywords request failed.',
    },
};
const AD_BREAK_COMPLETED_KEYS = [
    'adCreativeId',
    'adNetworkName',
    'adRequestId',
    'adSlot',
    'adType',
    'adUnitId',
    'currentVideoaudiorollIdx',
    'noOfVideoaudiorollsReceived',
    'noOfVideoaudiorollsPlayed',
];
const AD_CLICK_KEYS = [
    'adCreativeId',
    'adNetworkName',
    'adRequestId',
    'adSlot',
    'adType',
    'adUnitId',
    'currentVideoaudiorollIdx',
    'noOfVideoaudiorollsReceived',
    'destinationUrl',
];
const AD_REQUESTED_KEYS = [
    'adUnitId',
    'adNetworkName',
    'adRequestId',
    'adSlot',
    'adType',
    'adRequestDefaultTimer',
    'midrollTimerInSeconds',
    'maxRolls',
    'maxTotalRolls',
    'noOfVideoaudiorollRequested',
];
const AD_REQUEST_ERROR_CODES = [
    ERROR_CODES.VAST_EMPTY_RESPONSE,
    ERROR_CODES.VAST_NO_ADS_AFTER_WRAPPER,
    ERROR_CODES.FAILED_TO_REQUEST_ADS,
    ERROR_CODES.VAST_LOAD_TIMEOUT,
    ErrorCode.AdNoFillError,
    ErrorCode.AdSDKNotLoaded,
    ErrorCode.AdSDKLoadError,
    ErrorCode.AdNoConfigError,
    ErrorCode.RequestTimeout,
];
export class BaseAdReporter {
    // Track each ad slot by its pod position so we can infer failures when later
    // slots load without the earlier loaded slot ever starting.
    #trackedAdEventsByPosition = new Map();
    // IMA may revise total-ad counts as a request progresses, and sparse terminal
    // events like alladscomplete may not carry the latest value. Track the newest
    // observed count so later telemetry uses the request's latest known total.
    #latestKnownTotalAds;
    // A single request can surface multiple distinct playback failures as later
    // ads fail or drop. Deduplicate exact repeats while still allowing separate
    // playback failures within the same request to be reported. Request-classified
    // failures are stricter: emit at most one per request.
    #reportedErrorSignatures = new Set();
    #requestOutcome;
    #wasAdCancelled = false;
    #wasAdSkipped = false;
    get cachedAdEvent() {
        return this.#getLastTrackedAdEvent();
    }
    get wasAdCancelled() {
        return this.#wasAdCancelled;
    }
    get wasAdSkipped() {
        return this.#wasAdSkipped;
    }
    getPlayedTrackedAdEventsCount() {
        return Array.from(this.#trackedAdEventsByPosition.values()).filter(
        // Skipped ads flow through reportAdComplete(), so a completed state
        // represents any ad that terminally counted as played.
        ({ state }) => state === 'completed').length;
    }
    getReportingAdEvent(adEvent) {
        const trackedAdEvent = typeof adEvent?.adPosition === 'number'
            ? this.#trackedAdEventsByPosition.get(adEvent.adPosition)
            : this.#getLastTrackedAdEvent();
        if (!trackedAdEvent && !adEvent) {
            return undefined;
        }
        const mergedAdEvent = {
            ...trackedAdEvent,
            ...adEvent,
            totalAds: adEvent?.totalAds ?? this.#latestKnownTotalAds ?? trackedAdEvent?.totalAds,
        };
        return {
            ...mergedAdEvent,
            adType: 'client',
            eventType: mergedAdEvent.eventType || trackedAdEvent?.eventType || 'adloaded',
        };
    }
    getCompletionAdEvent(adEvent) {
        this.#updateLatestKnownTotalAds(adEvent.totalAds);
        const latestRequestEvent = this.getReportingAdEvent(adEvent);
        if (!latestRequestEvent) {
            return undefined;
        }
        // Exhaustion without any ad response data is still a no-fill/request-failed
        // path, not a completed ad break.
        if (this.#requestOutcome !== 'response_received') {
            return undefined;
        }
        const lastPlayedAdEvent = this.#getLastPlayedTrackedAdEvent();
        if (!lastPlayedAdEvent) {
            return {
                ...latestRequestEvent,
                adPosition: 0,
            };
        }
        return {
            ...latestRequestEvent,
            adPosition: lastPlayedAdEvent.adPosition,
            bitrate: lastPlayedAdEvent.bitrate ?? latestRequestEvent.bitrate,
            contentType: lastPlayedAdEvent.contentType || latestRequestEvent.contentType,
            creativeId: lastPlayedAdEvent.creativeId || latestRequestEvent.creativeId,
            destinationUrl: lastPlayedAdEvent.destinationUrl || latestRequestEvent.destinationUrl,
            height: lastPlayedAdEvent.height ?? latestRequestEvent.height,
            id: lastPlayedAdEvent.id ?? latestRequestEvent.id,
            width: lastPlayedAdEvent.width ?? latestRequestEvent.width,
        };
    }
    isCurrentRequest(requestId) {
        const sessionAdRequestId = this.sessionAdRequestId;
        // This can happen before a request exists at all, like an early no-config error. In this case, we want to return true to ungate reporting.
        if (!sessionAdRequestId) {
            return true;
        }
        // Once we have a session ad request id, we need an event request id to validate against it.
        // Without it, we cannot tell whether this belongs to the active request or to some stale/invalid callback.
        if (!requestId) {
            return false;
        }
        return requestId === sessionAdRequestId;
    }
    resetReportingState() {
        this.#reportedErrorSignatures.clear();
        this.#requestOutcome = undefined;
        this.#trackedAdEventsByPosition.clear();
        this.#latestKnownTotalAds = undefined;
        this.#wasAdCancelled = false;
        this.#wasAdSkipped = false;
    }
    markAdCancelled() {
        this.#wasAdCancelled = true;
    }
    markAdSkipped() {
        this.#wasAdSkipped = true;
    }
    getAdsBiddingParams(bidEvent) {
        const { requestId, unitId, network } = bidEvent;
        return {
            adRequestId: requestId,
            adUnitId: unitId,
            adNetworkName: network,
            adType: AD_MEDIUM_TYPES.VIDEO,
            adSlot: this.getAdsBiddingAdSlot(),
            adSize: '0x0',
        };
    }
    #getErrorAdEvent(requestId) {
        const latestTrackedAdEvent = this.#getLastTrackedAdEvent();
        if (latestTrackedAdEvent && (!requestId || latestTrackedAdEvent.requestId === requestId)) {
            return latestTrackedAdEvent;
        }
        if (!requestId) {
            return undefined;
        }
        return {
            adType: 'client',
            eventType: 'aderror',
            requestId,
        };
    }
    #getAdErrorDescription(errorCode, isAdRequestError) {
        const prefix = this.getAdErrorPrefix();
        switch (errorCode) {
            case ERROR_CODES.VAST_EMPTY_RESPONSE:
            case ERROR_CODES.VAST_NO_ADS_AFTER_WRAPPER:
                return `${prefix}, Request Failed`;
            case ERROR_CODES.VAST_LOAD_TIMEOUT:
                return `${prefix}, Playback Timeout`;
            case ErrorCode.AdNoFillError:
                return `${prefix}, No Fill`;
            case ErrorCode.RequestTimeout:
                return `${prefix}, Request Timeout`;
            case ErrorCode.AdSDKLoadError:
            case ErrorCode.AdSDKNotLoaded:
                return `${prefix}, SDK Load Failed`;
            case ErrorCode.AdNoConfigError:
                return `${prefix}, Missing Ad Config`;
            case ERROR_CODES.FAILED_TO_REQUEST_ADS:
                return `${prefix}, Error 1005 - Ad request url: ${this.adRequestUrl || 'unknown'}`;
            default:
                return isAdRequestError
                    ? `${prefix}, Unknown Ad Request Error`
                    : `${prefix}, Unknown Ad Playback Error`;
        }
    }
    #getErrorSignature(errorCode, errorMessage, errorAdEvent, requestId, isAdRequestError = false) {
        return [
            requestId || this.sessionAdRequestId || 'unknown',
            isAdRequestError ? 'request' : 'playback',
            errorCode.toString(),
            errorMessage,
            errorAdEvent?.adPosition?.toString() || '',
            errorAdEvent?.creativeId || '',
        ].join('|');
    }
    #updateLatestKnownTotalAds(totalAds) {
        if (typeof totalAds === 'number') {
            this.#latestKnownTotalAds = totalAds;
        }
    }
    #getTrackedAdState(existingState, nextState) {
        switch (existingState) {
            case 'completed':
            case 'failed':
                return existingState;
            case 'started':
                return nextState === 'completed' ? 'completed' : 'started';
            default:
                return nextState;
        }
    }
    #trackAdEvent(adEvent, state) {
        this.#updateLatestKnownTotalAds(adEvent.totalAds);
        if (typeof adEvent.adPosition !== 'number') {
            return undefined;
        }
        const existingTrackedAdEvent = this.#trackedAdEventsByPosition.get(adEvent.adPosition);
        const trackedAdEvent = {
            ...existingTrackedAdEvent,
            ...adEvent,
            adType: 'client',
            eventType: adEvent.eventType || existingTrackedAdEvent?.eventType || 'adloaded',
            totalAds: adEvent.totalAds ?? this.#latestKnownTotalAds ?? existingTrackedAdEvent?.totalAds,
            state: this.#getTrackedAdState(existingTrackedAdEvent?.state, state),
        };
        // Reinsert the updated entry so Map iteration order keeps the most recently
        // updated ad at the end for #getLastTrackedAdEvent().
        this.#trackedAdEventsByPosition.delete(adEvent.adPosition);
        this.#trackedAdEventsByPosition.set(adEvent.adPosition, trackedAdEvent);
        return trackedAdEvent;
    }
    #getLastTrackedAdEvent() {
        return Array.from(this.#trackedAdEventsByPosition.values()).at(-1);
    }
    #getLastPlayedTrackedAdEvent() {
        return Array.from(this.#trackedAdEventsByPosition.values())
            .reverse()
            .find(({ state }) => state === 'completed');
    }
    #reportPlaybackFailed(errorAdEvent, errorCode, errorMessage, debugDescription) {
        api.telemetry.reportAdsPlaybackFailed({
            ...pick(this.getAdParameters(errorAdEvent), [
                'adUnitId',
                'adNetworkName',
                'adRequestId',
                'adSlot',
                'adType',
                'adRequestDefaultTimer',
                'midrollTimerInSeconds',
                'bitrate',
                'noOfVideoaudiorollsReceived',
                'currentVideoaudiorollIdx',
                'adCreativeId',
                'isStreamPrebuffered',
                'prebufferTimeMs',
                'props',
            ]),
            errorCode,
            errorMessage,
            debugDescription,
        });
    }
    #reportFailureForLoadedAdThatNeverStarted(nextAdEvent) {
        const lastTrackedAdEvent = this.#getLastTrackedAdEvent();
        if (!lastTrackedAdEvent ||
            lastTrackedAdEvent.state !== 'loaded' ||
            typeof lastTrackedAdEvent.adPosition !== 'number' ||
            typeof nextAdEvent.adPosition !== 'number' ||
            nextAdEvent.adPosition <= lastTrackedAdEvent.adPosition) {
            return;
        }
        const failedTrackedAdEvent = this.#trackAdEvent(lastTrackedAdEvent, 'failed') || lastTrackedAdEvent;
        this.#reportPlaybackFailed(failedTrackedAdEvent, ErrorCode.Error.toString(), 'Ad loaded but playback never started before a later pod item loaded', `${this.getAdErrorPrefix()}, Playback never started`);
    }
    #reportResponseReceived(adEvent) {
        if (this.#requestOutcome) {
            return;
        }
        this.#requestOutcome = 'response_received';
        api.telemetry.reportAdsVideoAudioRollResponseReceived(pick(this.getAdParameters(adEvent), [
            'adUnitId',
            'adNetworkName',
            'adRequestId',
            'adSlot',
            'adType',
            'adRequestDefaultTimer',
            'midrollTimerInSeconds',
            'bitrate',
            'noOfVideoaudiorollsReceived',
        ]));
    }
    reportAdLoaded(adEvent) {
        if (!this.isCurrentRequest(adEvent.requestId)) {
            return;
        }
        this.#reportFailureForLoadedAdThatNeverStarted(adEvent);
        // IMA does not guarantee a stable early ad count, but response-received is
        // still intended to mean "we got ad data back", not "playback has begun".
        // We therefore emit it from LOADED as an early best-effort signal and rely
        // on later terminal events for strict ad break completion telemetry.
        const trackedAdEvent = this.#trackAdEvent(adEvent, 'loaded') ?? this.getReportingAdEvent(adEvent);
        this.#reportResponseReceived(trackedAdEvent ?? adEvent);
    }
    reportAdStart(adEvent) {
        if (!this.isCurrentRequest(adEvent.requestId)) {
            return;
        }
        const existingTrackedAdEvent = typeof adEvent.adPosition === 'number'
            ? this.#trackedAdEventsByPosition.get(adEvent.adPosition)
            : undefined;
        if (existingTrackedAdEvent && existingTrackedAdEvent.state !== 'loaded') {
            return;
        }
        const reportingAdEvent = this.#trackAdEvent(adEvent, 'started') ?? this.getReportingAdEvent(adEvent);
        if (reportingAdEvent) {
            this.#reportResponseReceived(reportingAdEvent);
        }
        api.telemetry.reportAdsPlaybackStarted(pick(this.getAdParameters(reportingAdEvent ?? adEvent), [
            'adUnitId',
            'adNetworkName',
            'adRequestId',
            'adSlot',
            'adType',
            'adRequestDefaultTimer',
            'midrollTimerInSeconds',
            'bitrate',
            'noOfVideoaudiorollsReceived',
            'adCreativeId',
            'currentVideoaudiorollIdx',
            'isStreamPrebuffered',
            'prebufferTimeMs',
            'props',
        ]));
    }
    reportAdPause(adEvent) {
        if (!this.isCurrentRequest(adEvent.requestId)) {
            return;
        }
        api.telemetry.reportAdsPlaybackPaused({
            ...pick(this.getAdParameters(adEvent), [
                'adUnitId',
                'adNetworkName',
                'adRequestId',
                'adSlot',
                'adType',
                'noOfVideoaudiorollsReceived',
                'adCreativeId',
                'currentVideoaudiorollIdx',
                'isStreamPrebuffered',
                'prebufferTimeMs',
                'props',
            ]),
            debugDescription: `${this.getAdErrorPrefix()}, Playback Paused`,
        });
    }
    reportAdResume(adEvent) {
        if (!this.isCurrentRequest(adEvent.requestId)) {
            return;
        }
        api.telemetry.reportAdsPlaybackResumed(pick(this.getAdParameters(adEvent), [
            'adUnitId',
            'adNetworkName',
            'adRequestId',
            'adSlot',
            'adType',
            'noOfVideoaudiorollsReceived',
            'adCreativeId',
            'currentVideoaudiorollIdx',
            'isStreamPrebuffered',
            'prebufferTimeMs',
            'props',
        ]));
    }
    reportAdComplete(adEvent) {
        if (!this.isCurrentRequest(adEvent.requestId)) {
            return;
        }
        const trackedAdEvent = this.#trackAdEvent(adEvent, 'completed') ?? this.getReportingAdEvent(adEvent);
        api.telemetry.reportAdsPlaybackFinished(pick(this.getAdParameters(trackedAdEvent ?? adEvent), [
            'adUnitId',
            'adNetworkName',
            'adRequestId',
            'adSlot',
            'adType',
            'adRequestDefaultTimer',
            'midrollTimerInSeconds',
            'noOfVideoaudiorollsReceived',
            'adCreativeId',
            'currentVideoaudiorollIdx',
            'wasAdCancelled',
            'wasAdSkipped',
            'isStreamPrebuffered',
            'prebufferTimeMs',
            'props',
        ]));
    }
    onAdSkipped(adEvent) {
        if (!this.isCurrentRequest(adEvent.requestId)) {
            return;
        }
        this.markAdSkipped();
        this.reportAdComplete(adEvent);
    }
    reportAdClick(adEvent) {
        if (!this.isCurrentRequest(adEvent.requestId)) {
            return;
        }
        api.telemetry.reportAdsVideoAudioRollClicked(pick(this.getAdParameters(adEvent), AD_CLICK_KEYS));
    }
    reportAdBreakCompleted(adEvent) {
        if (!this.isCurrentRequest(adEvent.requestId)) {
            return;
        }
        this.finalizePendingLoadedAdFailure('Ad loaded but playback never started before request exhaustion', `${this.getAdErrorPrefix()}, Playback never started`);
        const completedAdEvent = this.getCompletionAdEvent(adEvent);
        if (!completedAdEvent) {
            return;
        }
        api.telemetry.reportAdsVideoAudioRollCompleted(pick(this.getAdParameters(completedAdEvent), AD_BREAK_COMPLETED_KEYS));
    }
    reportAdRequested() {
        api.telemetry.reportAdsVideoAudioRollRequested({
            ...pick(this.getAdParameters(), AD_REQUESTED_KEYS),
            adRequestHasAmazonKeywords: false,
            biddingNetworks: this.biddingNetworks,
        });
    }
    reportAdsBiddingRequested(bidEvent) {
        api.telemetry.reportAdsBiddingRequested(this.getAdsBiddingParams(bidEvent));
    }
    reportAdsBiddingResponseReceived(bidEvent) {
        api.telemetry.reportAdsBiddingResponseReceived(this.getAdsBiddingParams(bidEvent));
    }
    reportAdsBiddingRequestFailed(bidEvent, errorScope) {
        api.telemetry.reportAdsBiddingRequestFailed({
            ...this.getAdsBiddingParams(bidEvent),
            ...APS_BIDDING_FAILURE_METADATA[errorScope],
            isRequestCanceled: false,
            debugDescription: 'AmazonKeywords Error',
        });
    }
    // When a break exhausts or is interrupted, the final pod item may only have
    // emitted `adloaded`. Flush that pending attempt into a playback failure.
    finalizePendingLoadedAdFailure(errorMessage, debugDescription) {
        const latestTrackedAdEvent = this.#getLastTrackedAdEvent();
        if (!this.isCurrentRequest(latestTrackedAdEvent?.requestId)) {
            return;
        }
        if (latestTrackedAdEvent &&
            (latestTrackedAdEvent.state === 'started' ||
                latestTrackedAdEvent.state === 'completed' ||
                latestTrackedAdEvent.state === 'failed')) {
            return;
        }
        const errorCode = ErrorCode.Error.toString();
        const failedTrackedAdEvent = latestTrackedAdEvent?.state === 'loaded'
            ? (this.#trackAdEvent(latestTrackedAdEvent, 'failed') ?? latestTrackedAdEvent)
            : latestTrackedAdEvent;
        this.#reportPlaybackFailed(failedTrackedAdEvent, errorCode, errorMessage, debugDescription);
    }
    reportPlaybackNotStarted(errorMessage, debugDescription) {
        this.finalizePendingLoadedAdFailure(errorMessage, debugDescription);
    }
    reportNoFill(requestId) {
        const noFillError = new PlayerError('Ad no fill error', ErrorCode.AdNoFillError, ErrorType.PLAY_ERROR, ErrorName.AdError, false);
        this.reportError(noFillError, { requestId });
    }
    reportError(error, context) {
        const requestId = error?.requestId ?? context?.requestId;
        if (!this.isCurrentRequest(requestId)) {
            return;
        }
        const errorAdEvent = this.#getErrorAdEvent(requestId);
        let errorCode = -1;
        let errorMessage = '';
        if (error.isAdError) {
            errorCode = error.errorCode || errorCode;
            errorMessage = error.message;
        }
        else if (PlayerError.isPlayerError(error)) {
            errorCode = error.code || errorCode;
            errorMessage = error.message;
        }
        const isAdRequestError = AD_REQUEST_ERROR_CODES.includes(errorCode);
        const debugDescription = this.#getAdErrorDescription(errorCode, isAdRequestError);
        const errorSignature = this.#getErrorSignature(errorCode, errorMessage, errorAdEvent, requestId, isAdRequestError);
        if (isAdRequestError && this.#requestOutcome === 'response_received') {
            api.telemetry.logError({
                message: `${this.errorLogPrefix} suppressed request-classified error after response received`,
                context: {
                    adRequestId: this.sessionAdRequestId,
                    errorMessage: error?.message,
                    errorCode: error?.errorCode || error?.code,
                    requestOutcome: this.#requestOutcome,
                },
            });
            return;
        }
        if (isAdRequestError && this.#requestOutcome === 'request_failed') {
            api.telemetry.logError({
                message: `${this.errorLogPrefix} suppressed additional request-classified error after request failure reported`,
                context: {
                    adRequestId: requestId || this.sessionAdRequestId,
                    errorMessage: error?.message,
                    errorCode: error?.errorCode || error?.code,
                    requestOutcome: this.#requestOutcome,
                },
            });
            return;
        }
        if (this.#reportedErrorSignatures.has(errorSignature)) {
            api.telemetry.logError({
                message: `${this.errorLogPrefix} suppressed duplicate error`,
                context: {
                    adRequestId: requestId || this.sessionAdRequestId,
                    errorMessage: error?.message,
                    errorCode: error?.errorCode || error?.code,
                    errorSignature,
                },
            });
            return;
        }
        this.#reportedErrorSignatures.add(errorSignature);
        if (isAdRequestError) {
            this.#requestOutcome = 'request_failed';
            api.telemetry.reportAdsVideoAudioRollRequestFailed({
                ...pick(this.getAdParameters(errorAdEvent), [
                    'adUnitId',
                    'adNetworkName',
                    'adRequestId',
                    'adSlot',
                    'adType',
                    'adRequestDefaultTimer',
                    'midrollTimerInSeconds',
                ]),
                errorCode: errorCode.toString(),
                errorMessage,
                debugDescription,
            });
            return;
        }
        const errorAdPosition = typeof errorAdEvent === 'object' &&
            errorAdEvent !== null &&
            'adPosition' in errorAdEvent &&
            typeof errorAdEvent.adPosition === 'number'
            ? errorAdEvent.adPosition
            : undefined;
        const trackedAdEvent = typeof errorAdPosition === 'number'
            ? this.#trackedAdEventsByPosition.get(errorAdPosition)
            : undefined;
        if (trackedAdEvent?.state === 'started' || trackedAdEvent?.state === 'completed') {
            api.telemetry.logError({
                message: `${this.errorLogPrefix} suppressed playback failure after playback already started`,
                context: {
                    adPosition: trackedAdEvent.adPosition,
                    adRequestId: requestId || this.sessionAdRequestId,
                    errorCode,
                    errorMessage,
                },
            });
            return;
        }
        const failedTrackedAdEvent = trackedAdEvent?.state === 'loaded'
            ? (this.#trackAdEvent(trackedAdEvent, 'failed') ?? trackedAdEvent)
            : errorAdEvent;
        this.#reportPlaybackFailed(failedTrackedAdEvent, errorCode.toString(), errorMessage, debugDescription);
    }
}
